import { cn } from '@/lib/utils';

/**
 * Inline 531 brand mark.
 *
 * Rendered as inline SVG rather than `<Image src="/531logo.svg">` so it needs no
 * network request: the /531logo.svg asset resolves at the root and is routed
 * through Clerk + subscription middleware (the matcher excludes the /public path
 * prefix, not root-served asset files). For an unsubscribed user — e.g. a
 * brand-new signup on /get-started — enforceSubscription redirects the asset
 * request, so the <Image> version rendered a broken image. Inlining sidesteps
 * the middleware entirely.
 *
 * Colors use `currentColor`, and the default is `--primary-tint` — the brand
 * hue. Call sites used to pass `text-primary`, which is the near-neutral INK
 * token, so the mark rendered charcoal on every surface that bothered to colour
 * it and foreground-black on every surface that did not. A brand mark is the one
 * element that should never be guessed at per call site, so the colour lives
 * here; override it only where the mark sits on a filled brand surface.
 */
/**
 * The 531 wordmark. Callers set a height; the width follows the 56x32 viewBox.
 *
 * Inside a `Button`, size it as `[&&]:h-N [&&]:w-auto`. Button squares every
 * descendant svg at `size-4`, and a parent's `[&_svg]:` selector outranks this
 * component's own classes — the wordmark comes out 16x16 and unreadable.
 * `[&&]` doubles the class selector, which outranks it back.
 *
 * Do NOT reach for `[&_svg]:size-auto` on the Button: that beats the height
 * too, and an SVG with a viewBox and no intrinsic size collapses to 0x0.
 */
export function Logo531({ className }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 56 32"
			fill="none"
			role="img"
			aria-label="531 Social"
			xmlns="http://www.w3.org/2000/svg"
			className={cn('h-10 w-auto text-primary-tint', className)}
		>
			<rect x="1.5" y="1.5" width="53" height="29" stroke="currentColor" strokeWidth="3" />
			<path
				d="M39.7476 22.39V13.984H36.9756V11.986H38.0736C38.6016 11.986 39.0276 11.914 39.3516 11.77C39.6876 11.614 39.9276 11.368 40.0716 11.032C40.2276 10.684 40.3056 10.21 40.3056 9.61H42.4836V22.39H39.7476Z"
				fill="currentColor"
			/>
			<path
				d="M30.4166 22.678C28.8326 22.678 27.6086 22.318 26.7446 21.598C25.8926 20.866 25.4366 19.882 25.3766 18.646L28.1666 18.538C28.2266 19.174 28.4486 19.642 28.8326 19.942C29.2286 20.23 29.7566 20.374 30.4166 20.374C30.8486 20.374 31.2326 20.314 31.5686 20.194C31.9046 20.062 32.1686 19.864 32.3606 19.6C32.5646 19.336 32.6666 19.006 32.6666 18.61C32.6666 17.998 32.4566 17.542 32.0366 17.242C31.6166 16.93 31.0466 16.774 30.3266 16.774H29.1926V14.74H30.3266C30.8786 14.74 31.3346 14.626 31.6946 14.398C32.0666 14.158 32.2526 13.756 32.2526 13.192C32.2526 12.712 32.1026 12.334 31.8026 12.058C31.5026 11.77 31.0286 11.626 30.3806 11.626C29.7326 11.626 29.2466 11.758 28.9226 12.022C28.5986 12.286 28.4006 12.628 28.3286 13.048L25.5746 12.904C25.6946 11.824 26.1686 10.96 26.9966 10.312C27.8366 9.65201 28.9646 9.32201 30.3806 9.32201C31.3886 9.32201 32.2406 9.46601 32.9366 9.75401C33.6326 10.042 34.1606 10.456 34.5206 10.996C34.8806 11.536 35.0606 12.172 35.0606 12.904C35.0606 13.66 34.8086 14.284 34.3046 14.776C33.8126 15.268 33.0986 15.604 32.1626 15.784V15.478C33.2306 15.634 34.0526 16 34.6286 16.576C35.2046 17.152 35.4926 17.896 35.4926 18.808C35.4926 19.612 35.2826 20.308 34.8626 20.896C34.4426 21.472 33.8546 21.916 33.0986 22.228C32.3426 22.528 31.4486 22.678 30.4166 22.678Z"
				fill="currentColor"
			/>
			<path
				d="M18.5204 22.678C17.4764 22.678 16.5944 22.522 15.8744 22.21C15.1544 21.898 14.5964 21.454 14.2004 20.878C13.8044 20.302 13.5764 19.63 13.5164 18.862L16.2884 18.736C16.3964 19.288 16.6364 19.702 17.0084 19.978C17.3924 20.242 17.8964 20.374 18.5204 20.374C19.2164 20.374 19.7624 20.176 20.1584 19.78C20.5664 19.384 20.7704 18.838 20.7704 18.142C20.7704 17.686 20.6744 17.29 20.4824 16.954C20.3024 16.618 20.0444 16.354 19.7084 16.162C19.3724 15.97 18.9704 15.874 18.5024 15.874C17.9864 15.874 17.5424 15.988 17.1704 16.216C16.7984 16.444 16.5404 16.732 16.3964 17.08H13.6604L14.5424 9.61H22.6964V11.914H16.8644L16.3964 15.64L16.0904 15.37C16.2944 15.058 16.5464 14.794 16.8464 14.578C17.1584 14.35 17.5184 14.176 17.9264 14.056C18.3344 13.924 18.7844 13.858 19.2764 13.858C20.1644 13.858 20.9264 14.044 21.5624 14.416C22.2104 14.788 22.7084 15.298 23.0564 15.946C23.4044 16.594 23.5784 17.326 23.5784 18.142C23.5784 19.066 23.3684 19.87 22.9484 20.554C22.5284 21.238 21.9404 21.766 21.1844 22.138C20.4284 22.498 19.5404 22.678 18.5204 22.678Z"
				fill="currentColor"
			/>
		</svg>
	);
}
